import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Typography,
    FormControl, InputLabel, Select, MenuItem, IconButton, Switch, FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import adminService from '../../../services/adminService';
import AdminToolbar from '../components/AdminToolbar';

const PromotionManagement = () => {
    const [promotions, setPromotions] = useState([]);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('all');

    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        code: '', type: 'PERCENT', value: '', maxDiscountValue: '', usageLimit: 100, isActive: true
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchPromotions();
    }, [search, filterType]);

    const fetchPromotions = async () => {
        const params = {};
        if (search) params.code = search; // API uses 'code' for search
        if (filterType !== 'all') params.type = filterType;

        const res = await adminService.getPromotions(params);
        if (res.success) setPromotions(res.data);
    };

    const [editingId, setEditingId] = useState(null);

    // Helper: Format number to VN currency string (using comma separator)
    const formatInputMoney = (value) => {
        if (!value) return '';
        let str = String(value);
        // Handle decimal from DB (e.g. 1000.00 -> 1000) to avoid 100000
        if (str.includes('.') && !str.includes(',')) {
            str = str.split('.')[0];
        }
        const numberStr = str.replace(/\D/g, '');
        return numberStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    // Helper: Parse formatted string back to number
    const parseInputMoney = (value) => {
        if (!value) return 0;
        return Number(String(value).split(',').join(''));
    };

    const handleOpenDialog = (promo = null) => {
        setErrors({});
        setOpenDialog(true);
        if (promo) {
            setEditingId(promo.id);
            setFormData({
                code: promo.code,
                type: promo.type,
                // Format if fixed value, raw if percent (or handle both if user wants uniform feeling)
                value: promo.type === 'PERCENT' ? promo.value : formatInputMoney(promo.value),
                maxDiscountValue: formatInputMoney(promo.maxDiscountValue || ''),
                usageLimit: promo.usageLimit,
                isActive: promo.isActive
            });
        } else {
            setEditingId(null);
            setFormData({ code: '', type: 'PERCENT', value: '', maxDiscountValue: '', usageLimit: 100, isActive: true });
        }
    };

    const handleSave = async () => {
        const newErrors = {};
        const rawValue = parseInputMoney(formData.value);
        const rawMaxDiscount = parseInputMoney(formData.maxDiscountValue);

        // Validation
        if (!formData.code) newErrors.code = 'Vui lòng nhập Mã Code';

        if (!formData.value) newErrors.value = 'Vui lòng nhập Giá Trị';
        else if (rawValue <= 0) newErrors.value = 'Giá trị phải lớn hơn 0';
        else if (formData.type === 'PERCENT' && rawValue > 100) newErrors.value = 'Phần trăm không được quá 100%';

        if (formData.maxDiscountValue && rawMaxDiscount < 0) {
            newErrors.maxDiscountValue = 'Giảm tối đa không được âm';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Vui lòng kiểm tra lại thông tin!');
            return;
        }

        const submitData = {
            ...formData,
            value: rawValue,
            maxDiscountValue: rawMaxDiscount || null
        };

        try {
            if (editingId) {
                await adminService.updatePromotion(editingId, submitData);
                toast.success('Cập nhật mã thành công!');
            } else {
                await adminService.createPromotion(submitData);
                toast.success('Tạo mã mới thành công!');
            }
            fetchPromotions();
            setOpenDialog(false);
        } catch (error) {
            toast.error('Lỗi: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa mã giảm giá này?')) {
            try {
                await adminService.deletePromotion(id);
                toast.success('Đã xóa mã giảm giá!');
                fetchPromotions();
            } catch (error) {
                toast.error('Lỗi xóa mã: ' + error.message);
            }
        }
    };

    const handleInputChange = (field, value) => {
        let newValue = value;
        if (field === 'value' || field === 'maxDiscountValue') {
            // Only format if NOT percent (for value)
            if (field === 'value' && formData.type === 'PERCENT') {
                newValue = value;
            } else {
                newValue = formatInputMoney(value);
            }
        }

        setFormData({ ...formData, [field]: newValue });
        if (errors[field]) {
            setErrors({ ...errors, [field]: null });
        }
    };

    return (
        <Box>
            <ToastContainer position="top-right" autoClose={3000} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#f1f5f9', mb: 3 }}>Quản Lý Mã Giảm Giá</Typography>

            <AdminToolbar
                searchValue={search}
                onSearchChange={setSearch}
                filterValue={filterType}
                onFilterChange={setFilterType}
                filterOptions={[
                    { value: 'all', label: 'Tất cả loại' },
                    { value: 'PERCENT', label: 'Phần trăm (%)' },
                    { value: 'FIXED_AMOUNT', label: 'Số tiền cố định' }
                ]}
                onCreate={() => handleOpenDialog(null)}
                createLabel="Tạo Mã Mới"
                placeholder="Tìm theo mã code..."
            />

            <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#0f172a' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Mã Code</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Loại</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Giá Trị</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Giới Hạn Giảm</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Lượt Dùng</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Ngày Tạo</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Trạng Thái</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Hành Động</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {promotions.map((promo) => (
                            <TableRow key={promo.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                <TableCell>
                                    <Typography fontWeight="bold" sx={{ color: '#60a5fa' }}>{promo.code}</Typography>
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>{promo.type === 'PERCENT' ? 'Phần trăm (%)' : 'Số tiền cố định'}</TableCell>
                                <TableCell sx={{ color: '#4ade80', fontWeight: 'bold' }}>{Number(promo.value).toLocaleString()}</TableCell>
                                <TableCell sx={{ color: 'white' }}>{promo.maxDiscountValue ? Number(promo.maxDiscountValue).toLocaleString() : '-'}</TableCell>
                                <TableCell sx={{ color: 'white' }}>{promo.usedCount} / {promo.usageLimit}</TableCell>
                                <TableCell sx={{ color: 'white' }}>{new Date(promo.createdAt).toLocaleDateString('vi-VN')}</TableCell>
                                <TableCell>
                                    {promo.isActive ? <Chip label="Hoạt động" color="success" size="small" /> : <Chip label="Đã khóa" color="default" size="small" />}
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleOpenDialog(promo)} sx={{ color: '#60a5fa' }}><EditIcon /></IconButton>
                                    <IconButton onClick={() => handleDelete(promo.id)} sx={{ color: '#f87171' }}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>{editingId ? "Cập Nhật Mã" : "Tạo Mã Mới"}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth margin="normal" label="Mã Code (Ví dụ: SALE50)"
                        value={formData.code}
                        onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                        error={!!errors.code}
                        helperText={errors.code}
                        disabled={!!editingId} // Disable code edit
                    />
                    {/* ... Rest of form uses handleInputChange ... */}
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Loại Giảm Giá</InputLabel>
                        <Select
                            value={formData.type}
                            label="Loại Giảm Giá"
                            onChange={(e) => handleInputChange('type', e.target.value)}
                        >
                            <MenuItem value="PERCENT">Theo Phần Trăm (%)</MenuItem>
                            <MenuItem value="FIXED_AMOUNT">Số Tiền Cố Định (VND)</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth margin="normal"
                        label={formData.type === 'PERCENT' ? "Giá Trị (%)" : "Giá Trị (VND)"}
                        value={formData.value}
                        onChange={(e) => handleInputChange('value', e.target.value)}
                        error={!!errors.value}
                        helperText={errors.value}
                        type={formData.type === 'PERCENT' ? "number" : "text"}
                    />
                    {formData.type === 'PERCENT' && (
                        <TextField
                            fullWidth margin="normal" label="Giảm Tối Đa (VND)"
                            value={formData.maxDiscountValue}
                            onChange={(e) => handleInputChange('maxDiscountValue', e.target.value)}
                            error={!!errors.maxDiscountValue}
                            helperText={errors.maxDiscountValue}
                        />
                    )}
                    <TextField
                        fullWidth margin="normal" label="Giới Hạn Lượt Dùng" type="number"
                        value={formData.usageLimit}
                        onChange={(e) => handleInputChange('usageLimit', e.target.value)}
                    />
                    {editingId && (
                        <FormControlLabel
                            control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />}
                            label="Kích hoạt"
                            sx={{ mt: 2 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Hủy</Button>
                    <Button onClick={handleSave} variant="contained">{editingId ? "Cập Nhật" : "Tạo Ngay"}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PromotionManagement;
